import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './VideoSubtitles.module.scss';
import useWindowSize from '../../hooks/useWindowSize';
import { INIT_SUBTITLES_POSITION } from '../../features/SubtitlesEditor/const';
import { roundToDigit } from '../../utils';
import { useSubtitlesStyles } from 'engage/hooks/useSubtitlesStyles';

const MOVED_SUBTITLES_POSITION = 90;
const MOVED_MOBILE_SUBTITLES_POSITION = 35;

const MAX_WORD_LENGTH = 20;

const VideoSubtitles = ({
  playerNode,
  subtitlesSettings,
  className,
  text,
  videoControlsVisible,
  isFullScreen,
  isMobileView = false,
  sceneId,
  videoMaxDimensions,
  mixedOrientation,
}) => {
  const [linesArray, setLinesArray] = useState([]);
  const { width } = useWindowSize();
  const [position, setPosition] = useState({ bottom: INIT_SUBTITLES_POSITION });
  const [isShowSubtitles, setIsShowSubtitles] = useState(false);
  const [transitionStyles, setTransitionStyles] = useState(false);

  const subtitleRef = useRef(null);

  const { customStyles, maxSymbolsInString } = useSubtitlesStyles({
    playerNode,
    subtitlesStyles: subtitlesSettings,
    isFullScreen,
    mixedOrientation,
    videoMaxDimensions,
  })

  const definePosition = (tries = 0) => {
    let video = {};

    // For clip organizer (multiplyMode)
    if (sceneId) {
      const container = document.getElementById(sceneId);
      video.offsetWidth = container?.offsetWidth;
      video.offsetHeight = container?.offsetHeight;

      video.videoWidth = container?.offsetWidth;
      video.videoHeight = container?.offsetHeight;
    } else {
      video = playerNode?.querySelector('video') || document.querySelector('video');
    }

    let defaultPosition = INIT_SUBTITLES_POSITION;
    if (subtitlesSettings) {
      defaultPosition = subtitlesSettings.marginY;
    }

    if (video?.videoHeight > 0 && video?.videoWidth > 0) {
      let contentHeight = video.offsetHeight;
      const playerRatio = video.offsetWidth / video.offsetHeight;
      if (videoMaxDimensions && roundToDigit(playerRatio) < roundToDigit(videoMaxDimensions?.width)) {
        contentHeight = video.offsetWidth / videoMaxDimensions.width;
      }
      const videoRatio = video.videoWidth / video.videoHeight;

      const marginPercent = defaultPosition / 100;

      const playerContentStartPosition = contentHeight < video.offsetHeight ? (video.offsetHeight - contentHeight) / 2 : 0;

      const bottom = playerContentStartPosition + (contentHeight * marginPercent - subtitleRef.current?.clientHeight * marginPercent);

      if (isMobileView) {
        if (isFullScreen) {
          if (playerRatio < 1) {
            contentHeight = video.offsetWidth / videoRatio;
          }

          if (videoControlsVisible) {
            contentHeight -= MOVED_MOBILE_SUBTITLES_POSITION;
          }

          const mobileFullScreenBottom = contentHeight * marginPercent - subtitleRef.current?.clientHeight * marginPercent;

          const calculatedMobileFullScreenBottom = (video.offsetHeight - contentHeight) / 2 + mobileFullScreenBottom;
          setPosition({ bottom: calculatedMobileFullScreenBottom });
        } else {
          if (videoControlsVisible) {
            setPosition({ top: MOVED_MOBILE_SUBTITLES_POSITION });
          } else {
            setPosition({ bottom });
          }
        }

        if (subtitleRef.current?.clientHeight) {
          setIsShowSubtitles(true);
          // Add transition styles only after the first appearance
          setTimeout(() => {
            setTransitionStyles({
              transition: 'bottom 250ms ease-in-out',
              transitionDelay: '100ms',
            });
          }, 0);
        } else {
          setIsShowSubtitles(false);
          setTransitionStyles({});
        }

        return;
      }

      if (videoControlsVisible && bottom < MOVED_SUBTITLES_POSITION) {
        setPosition({ bottom: MOVED_SUBTITLES_POSITION });
      } else {
        setPosition({ bottom });
      }

      if (subtitleRef.current?.clientHeight) {
        setIsShowSubtitles(true);
        // Add transition styles only after the first appearance
        setTimeout(() => {
          setTransitionStyles({
            transition: 'bottom 250ms ease-in-out',
            transitionDelay: '100ms',
          });
        }, 0);
      } else {
        setIsShowSubtitles(false);
        setTransitionStyles({});
      }
      return;
    }

    if (tries < 10) {
      setTimeout(() => {
        definePosition(tries + 1);
      }, 250);
    } else {
      setPosition({ bottom: defaultPosition });
    }
  };

  useEffect(() => {
    definePosition();
  }, [subtitlesSettings, isFullScreen, width, videoControlsVisible, subtitleRef.current?.clientHeight, linesArray]);

  useEffect(() => {
    if (maxSymbolsInString) {
      const originalLines = text?.split('\n');
      const textLines = [];
      let currentLine = '';
      originalLines?.forEach(originalLine => {
        if (originalLine.length > 0) {
          const splittedPhrase = originalLine.split(' ');
          splittedPhrase.forEach((splittedItem, index) => {
            let lineLength = currentLine.length;

            let lastCharacter = index == splittedPhrase.length - 1 ? '' : ' ';

            const splitItemWithSpace = splittedItem + lastCharacter;
            if (lineLength + splitItemWithSpace.length <= maxSymbolsInString) {
              currentLine += splitItemWithSpace;
              if (index == splittedPhrase.length - 1) {
                textLines.push(currentLine);
                currentLine = '';
              }
            } else {
              textLines.push(currentLine.trimEnd());
              currentLine = '';
              if (splitItemWithSpace.length > MAX_WORD_LENGTH) {
                let i = 0;
                while (i < splitItemWithSpace.length) {
                  if (i + maxSymbolsInString < splitItemWithSpace.length) {
                    length = i + maxSymbolsInString;
                  } else {
                    length = splittedItem.length - i;
                  }
                  currentLine += splitItemWithSpace.slice(i, i + maxSymbolsInString);
                  textLines.push(currentLine);
                  currentLine = '';
                  i += maxSymbolsInString;
                }
              } else {
                currentLine += splitItemWithSpace;
                if (index == splittedPhrase.length - 1) {
                  textLines.push(currentLine);
                  currentLine = '';
                }
              }
            }
          });
        }
      });
      if (currentLine.length > 0) {
        textLines.push(currentLine);
        currentLine = '';
      }
      setLinesArray(textLines);
    }
  }, [maxSymbolsInString, text]);

  useEffect(() => {
    return () => {
      setIsShowSubtitles(false);
      setTransitionStyles({});
    };
  }, [text]);

  return (
    <div
      ref={subtitleRef}
      className={cx(styles.wrapper, className)}
      style={{ ...position, ...transitionStyles, opacity: isShowSubtitles ? 1 : 0 }}
    >
      {linesArray?.length > 0
        ? linesArray.map((item, index) => (
            <span key={index} className={styles.textWrapper} style={{ ...customStyles }}>
              {item}
            </span>
          ))
        : null}
    </div>
  );
};

export default VideoSubtitles;
